import { format, startOfDay } from "date-fns";
import { useMemo, useState } from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ReferenceLine,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { Card } from "@/components/card";
import { LoadingScreen } from "@/components/loading-screen";
import { Badge } from "@/components/ui/badge";
import { ChartContainer } from "@/components/ui/chart";
import { ConcurrencyResponseItem } from "@classview/shared-types";

import { useGetConcurrency } from "../api/get-concurrency";
import { Timeframe } from "../types/timeframe";
import { generateTimeRangeFromCenter } from "../utils/generate-time-range";
import { getXAxisArgsForTimeBasedGraph } from "../utils/get-a-axis-args-for-time-based-graph";
import { groupChartData } from "../utils/group-chart-data";

import { ConcurrencyOverage } from "./concurrency-overages";
import { TimeframeSelector } from "./timeframe-selector";

export const VIDYO_CONCURRENCY_LIMIT = 4;

type ConcurrencyProps = {
  organisationId?: string;
};

export function Concurrency({ organisationId }: ConcurrencyProps) {
  const [timeframe, setTimeframe] = useState<Timeframe>("w");

  const { from, to } = generateTimeRangeFromCenter(timeframe);
  const {
    data: concurrency,
    isSuccess,
    isLoading,
  } = useGetConcurrency({
    from: from.toISOString(),
    to: to.toISOString(),
    organisationId,
  });

  const concurrencyWithUnixTime = useMemo(() => {
    return (
      concurrency?.map((item) => ({
        day: startOfDay(new Date(item.day)).getTime(),
        count: item.count,
      })) ?? []
    );
  }, [concurrency]);

  const groupedData = useMemo(
    () => (isSuccess ? groupChartData(concurrencyWithUnixTime, timeframe) : []),
    [concurrencyWithUnixTime, isSuccess, timeframe],
  );

  const now = startOfDay(new Date()).getTime();

  const mergedData = groupedData.map((item) => ({
    day: item.day,
    pastCount: item.day <= now ? item.count : null,
    futureCount: item.day >= now ? item.count : null,
  }));

  const xAxisArgs = getXAxisArgsForTimeBasedGraph(groupedData, timeframe);

  const daysWhereOverage: ConcurrencyResponseItem[] = useMemo(() => {
    const today = startOfDay(new Date()).toISOString();
    return isSuccess
      ? concurrency?.filter(
          (item) => item.count > VIDYO_CONCURRENCY_LIMIT && item.day >= today,
        )
      : [];
  }, [concurrency, isSuccess]);

  return (
    <Card>
      <div className="flex items-center justify-between">
        <Card.Header
          title={
            <span>
              Concurrency{" "}
              <Badge variant="outline" className="ml-1 font-normal">
                {format(from, "MMM dd")} - {format(to, "MMM dd")}
              </Badge>
            </span>
          }
        />
        <TimeframeSelector
          value={timeframe}
          onChange={(value) => setTimeframe(value as Timeframe)}
        />
      </div>

      <Card.Content className="relative">
        {isLoading && (
          <LoadingScreen className="absolute inset-0 z-10 h-full w-full" />
        )}
        <ChartContainer config={{}} className="h-[280px] w-full">
          <AreaChart data={mergedData}>
            <defs>
              <linearGradient id="pastGradient" x1="0" y1="0" x2="0" y2="1">
                <stop
                  offset="0%"
                  stopColor="hsl(var(--primary))"
                  stopOpacity={0.3}
                />
                <stop
                  offset="100%"
                  stopColor="hsl(var(--primary))"
                  stopOpacity={0}
                />
              </linearGradient>
              <linearGradient id="futureGradient" x1="0" y1="0" x2="0" y2="1">
                <stop
                  offset="0%"
                  stopColor="hsl(var(--muted))"
                  stopOpacity={0.2}
                />
                <stop
                  offset="100%"
                  stopColor="hsl(var(--muted))"
                  stopOpacity={0}
                />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="day" {...xAxisArgs} />
            <YAxis domain={[0, "auto"]} />
            <ReferenceLine
              x={now}
              stroke="hsl(var(--muted))"
              strokeDasharray="3 3"
              label={{
                value: "Now",
                position: "top",
                fill: "hsl(var(--muted))",
                fontSize: 12,
              }}
            />
            <ReferenceLine
              id="concurrency-hard-limit"
              y={VIDYO_CONCURRENCY_LIMIT}
              stroke="hsl(var(--card-foreground))"
              strokeDasharray="5 5"
              strokeWidth={2}
              label={{
                value: "Licence Threshold",
                position: "insideBottomLeft",
                fill: "hsl(var(--muted))",
                fontSize: 12,
              }}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: "#161B26",
                color: "hsl(var(--card-foreground))",
                borderRadius: "var(--radius)",
                padding: "12px",
                border: "none",
              }}
              labelFormatter={(value) => {
                const date = new Date(value);
                return format(date, "MMM dd, yyyy");
              }}
              labelClassName="text-white"
              formatter={(value, name) => {
                return [
                  <span key="value" className="font-medium">
                    {value} {name} Sessions
                  </span>,
                ];
              }}
            />

            {/* Past segment of the area */}
            <Area
              type="monotone"
              dataKey="pastCount"
              name="Past"
              stroke="#B692F6"
              fill="url(#pastGradient)"
              strokeWidth={2}
              dot={false}
              isAnimationActive={true}
            />

            {/* Future segment of the area */}
            <Area
              type="monotone"
              dataKey="futureCount"
              name="Future"
              stroke="hsl(var(--muted))"
              fill="url(#futureGradient)"
              strokeWidth={2}
              dot={false}
              strokeDasharray="4 5"
              isAnimationActive={true}
            />
          </AreaChart>
        </ChartContainer>
      </Card.Content>
      <ConcurrencyOverage days={daysWhereOverage} />
    </Card>
  );
}
